import { useTranslations } from 'next-intl';
import React, { useEffect, useRef, useState } from 'react';

interface DetailCountDownProps {
  time: number;
  refreshPageData: () => void;
  variant?: 'default' | 'light'; // light: 无背景色，文字#282828，不加粗
}

const DetailCountDown: React.FC<DetailCountDownProps> = ({
  time,
  refreshPageData,
  variant = 'default',
}) => {
  const t = useTranslations('goodsDetail.detailCountDown');
  const [remain, setRemain] = useState(time);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setRemain(time);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setRemain((prev) => {
        const newRemain = prev - 1;
        // 倒计时结束，刷新数据
        if (newRemain <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setTimeout(() => {
            refreshPageData();
          }, 2000);
          return 0;
        }
        return newRemain;
      });
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [time, refreshPageData]);

  // 页面可见性和路由回退监听，强制刷新
  // 有时候切换路由回来还保持上一次接口 props 拿到的数据，需要强制刷新
  useEffect(() => {
    const handleRefresh = () => {
      refreshPageData();
    };
    document.addEventListener('visibilitychange', handleRefresh);
    window.addEventListener('popstate', handleRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleRefresh);
      window.removeEventListener('popstate', handleRefresh);
    };
  }, [refreshPageData]);

  if (remain <= 0) return null;

  const day = Math.floor(remain / (60 * 60 * 24));
  const hr = Math.floor((remain / (60 * 60)) % 24);
  const min = Math.floor((remain / 60) % 60);
  const sec = Math.floor(remain % 60);

  const isLight = variant === 'light';

  return (
    <div
      className={`time-content flex items-center text-[12px] ${isLight ? 'text-[#282828]' : ''}`}
    >
      <span
        className={`end-word ${isLight ? 'font-normal text-[#282828]' : 'font-bb text-primary-black'}`}
      >
        {t('endsIn')}&nbsp;&nbsp;
      </span>
      {day >= 1 ? (
        <>
          <span className={isLight ? 'text-[#282828]' : ''}>{day}</span>
          <span className={isLight ? 'text-[#282828]' : ''}>
            {t('day')}
            {day > 1 ? t('daysSuffix') : ''}&nbsp;
          </span>
        </>
      ) : (
        <>
          <span
            className={`time ${isLight ? 'w-auto' : 'w-[20px]'} ${isLight ? 'h-auto' : 'h-[20px]'} rounded-[3px] text-center ${isLight ? '' : 'leading-[20px]'} ${isLight ? 'text-[12px]' : 'text-[11px]'} ${
              isLight ? 'bg-transparent text-[#282828]' : 'bg-[#222222] text-white'
            }`}
          >
            {hr > 9 ? hr : `0${hr}`}
          </span>
          <span className={`colon ${isLight ? 'text-[#282828]' : 'text-[#222222] mx-[3px]'}`}>
            :
          </span>
          <span
            className={`time ${isLight ? 'w-auto' : 'w-[20px]'} ${isLight ? 'h-auto' : 'h-[20px]'} rounded-[3px] text-center ${isLight ? '' : 'leading-[20px]'} ${isLight ? 'text-[12px]' : 'text-[11px]'} ${
              isLight ? 'bg-transparent text-[#282828]' : 'bg-[#222222] text-white'
            }`}
          >
            {min > 9 ? min : `0${min}`}
          </span>
          <span className={`colon ${isLight ? 'text-[#282828]' : 'text-[#222222] mx-[3px]'}`}>
            :
          </span>
          <span
            className={`time ${isLight ? 'w-auto' : 'w-[20px]'} ${isLight ? 'h-auto' : 'h-[20px]'} rounded-[3px] text-center ${isLight ? '' : 'leading-[20px]'} ${isLight ? 'text-[12px]' : 'text-[11px]'} ${
              isLight ? 'bg-transparent text-[#282828]' : 'bg-[#222222] text-white'
            }`}
          >
            {sec > 9 ? sec : `0${sec}`}
          </span>
        </>
      )}
    </div>
  );
};

export default DetailCountDown;
